import { useState, useEffect, useRef } from "react";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config";

/**
 * The cadence one KPI reports at, at the site being entered against.
 *
 * Frequency is configured per location and sub-location (Settings → KPIs →
 * Frequency), so the KPI list's `answerFrequency` — resolved company-wide when
 * the list was fetched — is only right until a data owner picks a site. This
 * re-resolves it for that site, and Add Data derives the reporting periods it
 * offers from the result.
 *
 * `fallback` is returned while the request is in flight and whenever it fails, so
 * the period dropdown always has a cadence to work from and a degraded frequency
 * service never blocks data entry. Responses are tagged with a fetch id, so a
 * slow answer for a previously selected location cannot overwrite a newer one.
 * @param {object} params the KPI, the year, the site and the fallback cadence.
 * @param {number} params.financialYearId the financial year being entered.
 * @param {number} params.kpiId the reporting question.
 * @param {number|null} params.locationId the chosen location; null for none yet.
 * @param {number|null} params.subLocationId the chosen sub-location, if any.
 * @param {string} params.fallback cadence to use until a scoped one arrives.
 * @param {boolean} [params.enabled] false suspends fetching (e.g. modal closed).
 * @returns {{frequency: string, loading: boolean, inherited: boolean}} the cadence in force.
 */
export function useEffectiveFrequency({
  financialYearId,
  kpiId,
  locationId,
  subLocationId,
  fallback,
  enabled = true,
}) {
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    if (!enabled || !financialYearId || !kpiId) {
      setResolved(null);
      return;
    }

    const fetchId = ++fetchIdRef.current;
    const isCurrent = () => fetchId === fetchIdRef.current;

    const load = async () => {
      setLoading(true);
      try {
        const params = { financialYearId, kpiId };
        if (locationId) params.locationId = locationId;
        if (locationId && subLocationId) params.subLocationId = subLocationId;

        const { isSuccess, data } = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}kpiFrequency/effective`,
          {},
          params,
          "GET",
        );
        if (!isCurrent()) return;
        setResolved(isSuccess ? data?.data ?? null : null);
      } catch (e) {
        console.error(e);
        if (isCurrent()) setResolved(null);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    load();
  }, [enabled, financialYearId, kpiId, locationId, subLocationId]);

  return {
    frequency: resolved?.frequency ?? fallback,
    inherited: resolved ? Boolean(resolved.inherited) : true,
    loading,
  };
}
